import { Component } from 'react';
import { setIn as FormikSetIn } from 'formik';

import { isFunction } from 'Lib/utilities/isFunction';

export const withForm = WrappedComponent => {
    class FormContainer extends Component {
        state = {
            error: false
        };

        notify = options => {
            if (this.props.notify) {
                this.props.notify(options);
            }
        };

        handleInvalid = (errors, config = {}) => {
            if (config.message) {
                let message = config.message;

                if (isFunction(config.message)) {
                    message = config.message(errors);
                }

                this.notify({
                    error: message
                });
            }
        };

        /**
         *	handlePromise: an event handler for `onSubmit` events for most of our forms. All calls run through the `callApi()` middleware
         *	when scaffolded in our `withApi()` Higher Order Component.
         *
         *	@param { object } form - The Formik form we are submitting
         *	@param { array } promises - An array of promises to call
         *	@param { object } config - A configurable with objects used to display messages, perform actions post asynchronous calls, etc.
         *		`Currently used/possible properties (likely more to come):`
         *			@property { function | string } successMessage - A message that will populate a notify the system after resolved Promise(s)
         *			@property { function | string } errorMessage - A message that will populate a notify the system after rejected Promise(s)
         *			@property { function | string } successRouteTo - A route to navigate to after resolved Promise(s)
         *			@property { function } successAction - A Redux action to dispatch after resolved Promise(s) @example successAction: reloadAccounts
         *
         *	@return { Promise } Returns a Promise that allows us to chain off of `.then()` or `.catch()` if at all needed for custom logic.
         *			For most use-cases, we generally handle all `.catch()` logic within this function and won't need to write your own.
         *			It will be common however to write your own logic in a `.then()` block for setting state after successful calls.
         *
         *	@example
         *
         *   this.props.form.handlePromise(form,
         *		[this.props.api.saveDeposit(null, data), ...insert multiple API calls here...], {
         *			successMessage: "Bank Deposit successfully recorded",
         *			successRouteTo: "/accounting/banking/deposits",
         *			errorMessage: "An error occurred while recording a deposit"
         *	});
         */
        handlePromise = (form, promises, config = {}) => {
            if (form.setProcessing) {
                form.setProcessing(true);
            } else {
                form.setSubmitting(true);
            }

            return Promise.all(promises)
                .then(response => {
                    if (this.props.fetchLogs) {
                        this.props.fetchLogs(true);
                    }

                    return new Promise(resolve => {
                        const extractedResponse = response.length < 2 ? response[0] : response;
                        /*
							Dispatches a redux action after a successful call
							(e.g. we need to navigate to another page and reload data for the screen to properly render)
						*/
                        if (config.successAction) {
                            config.successAction(extractedResponse);
                        }

                        if (config.successMessage) {
                            let successMessage = config.successMessage;

                            if (isFunction(config.successMessage)) {
                                successMessage = config.successMessage(extractedResponse);
                            }

                            this.notify({
                                success: successMessage
                            });
                        }

                        // Navigates to route after a resolved call if the `successRouteTo` property exists
                        if (this.props.route) {
                            if (config.successRouteTo) {
                                let url = config.successRouteTo;

                                if (isFunction(config.successRouteTo)) {
                                    url = config.successRouteTo(extractedResponse);
                                }

                                this.props.route.history.push(url);
                            }
                        }

                        if (form.setProcessing) {
                            form.setProcessing(false);
                        } else {
                            form.setSubmitting(false);
                        }

                        resolve(extractedResponse);
                    });
                })
                .catch(error => {
                    return new Promise((__, reject) => {
                        if (config.errorMessage) {
                            let errorMessage = config.errorMessage;

                            if (isFunction(config.errorMessage)) {
                                errorMessage = config.errorMessage(error.response);
                            }

                            this.notify({
                                error: errorMessage
                            });
                        }

                        if (error.response) {
                            this.setState({
                                error: error.response.data ? error.response.data : error.response
                            });

                            let formErrors = {};

                            if (error.response.data) {
                                Object.keys(error.response.data).forEach(fieldPath => {
                                    formErrors = FormikSetIn(formErrors, fieldPath, error.response.data[fieldPath]);
                                });
                            }

                            // Set Formik errors based off of backend error response
                            form.setErrors(formErrors);
                        }

                        reject(error);
                        form?.setSubmitting(false);
                    });
                });
        };

        handleErrors = (form, errors, config) => {
            form.setErrors(errors);

            if (config.errorMessage) {
                this.notify({ error: config.errorMessage });
            }
        };

        render() {
            const form = {
                handlePromise: this.handlePromise,
                handleErrors: this.handleErrors
            };
            const { error } = this.state;

            return <WrappedComponent form={form} {...this.props} error={error} notify={this.notify} />;
        }
    }

    FormContainer.displayName = `FormContainer${WrappedComponent.name}`;

    return FormContainer;
};

export default withForm;
